import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { i18n } from '~/utils';

import { type ParsingCaseActionMutationResponse } from '../types';
import { useParsingCaseActionMutationOptions } from '../useParsingCaseActionMutationOptions';

import { retryProcessParsingCase } from './retryProcessParsingCase';

/**
 * Custom hook to retry the processing step of a parsing case from its latest
 * PARSED_JSON artifact (skips OCR and parsing).
 *
 * @param options - Optional react-query mutation options
 * @returns The mutation result
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/post_retry_process_vadmin_parsing_cases__case_id__actions_retry_process_post
 */
export const useMutationRetryProcessParsingCase = (
  options?: UseMutationOptions<
    ParsingCaseActionMutationResponse,
    unknown,
    UUID
  >,
) => {
  const actionOptions = useParsingCaseActionMutationOptions<UUID>(() =>
    i18n.t(($) => $.retryProcessParsingCase.success, { ns: 'data' }),
  );

  return useMutation<ParsingCaseActionMutationResponse, unknown, UUID>({
    mutationFn: async (caseId: UUID) => retryProcessParsingCase(caseId),
    ...actionOptions,
    ...options,
  });
};
